import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import AnimateButton from '../../../Components/Theme/Extended/AnimateButton';
import SubmitButton from '../../../Components/Theme/Extended/SubmitButton';
import { palette } from '../../../styles/variables/colors';
import { FORM_STATUS } from './Departments';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { ErrorOutline } from '@mui/icons-material';
import { useEffect } from 'react';
import AuthSetAgent from '../../../Api/AuthSetAgent';
import { error, success } from '../../../Common/Toast/Toast';

const OrganizationsList = ({
  value,
  error,
  onChange,
  organizations,
  required = false,
  isEdit = false,
}) => {
  return (
    <Box className="mt-5">
      <FormControl error={error} required={required}>
        <FormLabel
          className="mb-2"
          id="organizations-radio-buttons-group-label"
        >
          <Typography variant="h5">
            Επιλέξτε τον Οργανισμό που ανήκει:
            {error && (
              <ErrorOutline
                color={palette.error.main}
                sx={{
                  fontSize: 18,
                  marginBottom: '1px',
                  marginLeft: 1,
                  color: palette.error.main,
                }}
              />
            )}
          </Typography>
        </FormLabel>
        <RadioGroup
          aria-labelledby="organizations-radio-buttons-group-label"
          value={value}
          onChange={onChange}
          className="ml-3"
        >
          {organizations?.map((item) => (
            <FormControlLabel
              key={item?.id}
              value={item?.id}
              control={<Radio disabled={isEdit} value={item?.id} />}
              label={item?.name}
            />
          ))}
        </RadioGroup>
      </FormControl>
    </Box>
  );
};

const SubOrganizationsList = ({
  value,
  error,
  onChange,
  subOrganizations,
  required = false,
  isEdit = false,
}) => {
  return (
    <Box className="mt-5">
      <FormControl error={error} required={required}>
        <FormLabel
          className="mb-2"
          id="suborganizations-radio-buttons-group-label"
        >
          <Typography variant="h5">
            Επιλέξτε την Υποοργανισμό που ανήκει:
            {error && (
              <ErrorOutline
                color={palette.error.main}
                sx={{
                  fontSize: 18,
                  marginBottom: '1px',
                  marginLeft: 1,
                  color: palette.error.main,
                }}
              />
            )}
          </Typography>
        </FormLabel>
        <RadioGroup
          aria-labelledby="sectors-radio-buttons-group-label"
          value={value}
          onChange={onChange}
          className="ml-3"
        >
          {!subOrganizations?.length ? (
            <Typography className="mt-2">
              Δεν υπάρχουν Υποοργανισμοί για αυτόν τον Οργανισμό
            </Typography>
          ) : (
            subOrganizations?.map((item) => (
              <FormControlLabel
                key={item?.id}
                value={item?.id}
                control={<Radio disabled={isEdit} value={item?.id} />}
                label={item?.name}
              />
            ))
          )}
        </RadioGroup>
      </FormControl>
    </Box>
  );
};

const SectorsList = ({
  value,
  error,
  onChange,
  sectors,
  required = false,
  isEdit = false,
}) => {
  return (
    <Box className="mt-5">
      <FormControl error={error} required={required}>
        <FormLabel
          className="mb-2"
          id="suborganizations-radio-buttons-group-label"
        >
          <Typography variant="h5">
            Επιλέξτε τον Τομέα που ανήκει:
            {error && (
              <ErrorOutline
                color={palette.error.main}
                sx={{
                  fontSize: 18,
                  marginBottom: '1px',
                  marginLeft: 1,
                  color: palette.error.main,
                }}
              />
            )}
          </Typography>
        </FormLabel>
        <RadioGroup
          aria-labelledby="sectors-radio-buttons-group-label"
          value={value}
          onChange={onChange}
          className="ml-3"
        >
          {!sectors?.length ? (
            <Typography className="mt-2">
              Δεν υπάρχουν καταχωρημένοι Τομείς για αυτόν τον Υποοργανισμό
            </Typography>
          ) : (
            sectors?.map((item) => (
              <FormControlLabel
                key={item?.id}
                value={item?.id}
                control={<Radio disabled={isEdit} value={item?.id} />}
                label={item?.name}
              />
            ))
          )}
        </RadioGroup>
      </FormControl>
    </Box>
  );
};

export const DEPARTMENT_FIELD = {
  Name: 'name',
  Email: 'email',
  Address: 'address',
  SectorId: 'sectorId',
  SubOrganizationId: 'subOrganizationId',
  OrganizationId: 'organizationId',
  Telephone: 'tel',
};

const SectorSchema = yup.object().shape({
  [DEPARTMENT_FIELD.Name]: yup.string().required(),
  [DEPARTMENT_FIELD.Email]: yup.string().email().notRequired(),
  [DEPARTMENT_FIELD.Address]: yup.string().notRequired(),
  [DEPARTMENT_FIELD.SectorId]: yup.string().required(),
  [DEPARTMENT_FIELD.SubOrganizationId]: yup.string().required(),
  [DEPARTMENT_FIELD.OrganizationId]: yup.string().notRequired(),
  [DEPARTMENT_FIELD.Telephone]: yup
    .string()
    .trim()
    .nullable()
    .transform((v, o) => (o === '' ? null : v)) // allow empty string
    .min(10)
    .max(10)
    .notRequired(),
});

const defaultValues = {
  [DEPARTMENT_FIELD.Name]: '',
  [DEPARTMENT_FIELD.SectorId]: null,
  [DEPARTMENT_FIELD.SubOrganizationId]: null,
  [DEPARTMENT_FIELD.OrganizationId]: null,
};

const DepartmentCreateEditForm = ({
  isEdit,
  item,
  handleAction,
  refreshList,
  sectors,
  subOrganizations,
  organizations,
}) => {
  const [actionLoading, setActionLoading] = useState(false);
  const { handleSubmit, control, reset, setValue, watch } = useForm({
    defaultValues: isEdit
      ? {
          [DEPARTMENT_FIELD.Name]: item?.[DEPARTMENT_FIELD.Name],
          [DEPARTMENT_FIELD.SubOrganizationId]:
            item?.[DEPARTMENT_FIELD.SubOrganizationId],
          [DEPARTMENT_FIELD.SectorId]: item?.[DEPARTMENT_FIELD.SectorId],
          [DEPARTMENT_FIELD.OrganizationId]:
            item?.[DEPARTMENT_FIELD.OrganizationId],
        }
      : defaultValues,
    resolver: yupResolver(SectorSchema),
  });

  const selectedOrganizationId = watch(DEPARTMENT_FIELD.OrganizationId);
  const selectedSubOrganizationId = watch(DEPARTMENT_FIELD.SubOrganizationId);
  const filteredSubOrganizations = subOrganizations?.filter(
    (subOrg) =>
      subOrg?.organizationId?.toString() === selectedOrganizationId?.toString()
  );
  const filteredSectors = sectors?.filter(
    (sector) =>
      sector?.subOrganizationId?.toString() ===
      selectedSubOrganizationId?.toString()
  );

  const submitCreate = async () => {
    if (actionLoading) {
      return;
    }

    setActionLoading(true);

    const formValues = watch();
    const formattedBody = {
      [DEPARTMENT_FIELD.Name]: formValues?.[DEPARTMENT_FIELD.Name],
      [DEPARTMENT_FIELD.SectorId]: parseInt(
        formValues?.[DEPARTMENT_FIELD.SectorId]
      ),
    };
    await AuthSetAgent.department
      .createDepartment(formattedBody)
      .then(async (resp) => {
        if (resp?.success === true) {
          success('To Τμήμα δημιουργήθηκε επιτυχώς');
        } else {
          error('Κάτι πήγε στραβά κατά την δημιουργία του τμήματος');
        }
        reset(defaultValues);
        handleAction({ formStatus: FORM_STATUS.HIDDEN });
        await refreshList();
      })
      .catch((e) => {
        error('Παρουσιάστηκε σφάλμα κατά τη δημιουργία Τμήματος!');
      })
      .finally(() => {
        setActionLoading(false);
      });
  };

  const submitEdit = async () => {
    if (actionLoading) {
      return;
    }

    setActionLoading(true);

    const formValues = watch();
    const formattedBody = {
      [DEPARTMENT_FIELD.Name]: formValues?.[DEPARTMENT_FIELD.Name],
    };
    await AuthSetAgent.department
      .updateDepartment(item?.id, formattedBody)
      .then(async (resp) => {
        if (resp?.success === true) {
          success('To Τμήμα ενημερώθηκε επιτυχώς');
        } else {
          error('Κάτι πήγε στραβά κατά την ενημέρωση του τμήματος');
        }
        handleAction({ formStatus: FORM_STATUS.HIDDEN });
        await refreshList();
      })
      .catch((e) => {
        error('Παρουσιάστηκε σφάλμα κατά την επεξεργασία Τμήματος!');
      })
      .finally(() => {
        setActionLoading(false);
      });
  };

  const onSubmit = async () => {
    isEdit ? await submitEdit() : await submitCreate();
  };

  useEffect(() => {
    if (!isEdit) {
      reset(defaultValues);
    } else {
      reset({
        [DEPARTMENT_FIELD.Name]: item?.[DEPARTMENT_FIELD.Name] || '',
        [DEPARTMENT_FIELD.SectorId]: item?.[DEPARTMENT_FIELD.SectorId] || null,
        [DEPARTMENT_FIELD.SubOrganizationId]:
          item?.[DEPARTMENT_FIELD.SubOrganizationId] || null,
        [DEPARTMENT_FIELD.OrganizationId]:
          item?.[DEPARTMENT_FIELD.OrganizationId] || null,
      });
    }
  }, [isEdit, item, reset]);

  return (
    <Box className="mr-5">
      <Typography className="pb-5" variant="h3" color={'#003375'} gutterBottom>
        {isEdit ? 'Επεξεργασία Τμήματος' : 'Προσθήκη Τμήματος'}
      </Typography>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box className="grid gap-3">
          <Controller
            name={DEPARTMENT_FIELD.Name}
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Box className="flex mb-2 col-span-12">
                <TextField
                  className="w-[50%]"
                  label="Ονομασία"
                  onChange={onChange}
                  value={value}
                  error={!!error}
                  helperText={
                    error && 'Το πεδίο Ονομασία Τμήματος είναι υποχρεωτικό!'
                  }
                />
              </Box>
            )}
          />
        </Box>

        <Controller
          name={DEPARTMENT_FIELD.OrganizationId}
          control={control}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <OrganizationsList
              error={!!error}
              onChange={(event) => {
                setValue(DEPARTMENT_FIELD.OrganizationId, event?.target?.value);
                if (selectedOrganizationId) {
                  setValue(DEPARTMENT_FIELD.SubOrganizationId, null);
                }
              }}
              organizations={organizations}
              value={value}
              isEdit={isEdit}
            />
          )}
        />

        <Controller
          name={DEPARTMENT_FIELD.SubOrganizationId}
          control={control}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <>
              {!!watch(DEPARTMENT_FIELD.OrganizationId) && (
                <Box className="ml-20">
                  <SubOrganizationsList
                    error={!!error}
                    onChange={onChange}
                    value={value}
                    subOrganizations={filteredSubOrganizations}
                    isEdit={isEdit}
                  />
                </Box>
              )}
            </>
          )}
        />

        <Controller
          name={DEPARTMENT_FIELD.SectorId}
          control={control}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <>
              {!!watch(DEPARTMENT_FIELD.SubOrganizationId) && (
                <Box className="ml-20">
                  <SectorsList
                    error={!!error}
                    onChange={onChange}
                    value={value}
                    sectors={filteredSectors}
                    isEdit={isEdit}
                  />
                </Box>
              )}
            </>
          )}
        />
        <Box className="flex justify-end pt-5">
          <AnimateButton>
            <SubmitButton
              sx={{
                backgroundColor: '#184682',
                color: '#f9f9f9',
                textTransform: 'none',
                marginRight: '15px',
                '&:hover': {
                  backgroundColor: '#184682',
                  color: '#f9f9f9',
                },
                fontSize: '1rem',
              }}
              onClick={() => {
                handleAction({ formStatus: FORM_STATUS.HIDDEN });
              }}
              variant="contained"
              title="Ακύρωση"
            />
          </AnimateButton>
          <AnimateButton>
            <SubmitButton
              sx={{
                backgroundColor: '#184682',
                color: '#f9f9f9',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#184682',
                  color: '#f9f9f9',
                },
                fontSize: '1rem',
              }}
              onClick={() => {}}
              variant="contained"
              type="submit"
              title="Υποβολή"
            />
          </AnimateButton>
        </Box>
      </form>
    </Box>
  );
};

export default DepartmentCreateEditForm;
